import React, { useEffect, useState } from 'react';

// Function to interpolate color based on percentage (starting from #18cec8)
const interpolateColor = (percentage: number) => {
  const clampedPct = Math.max(0, Math.min(100, percentage));

  if (clampedPct <= 50) {
    // Interpolate between teal (#18cec8) and orange (#ffa500)
    const ratio = clampedPct / 50;
    const r = Math.round(24 + (255 - 24) * ratio);
    const g = Math.round(206 + (165 - 206) * ratio);
    const b = Math.round(200 + (0 - 200) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Interpolate between orange (#ffa500) and red (#ff0000)
    const ratio = (clampedPct - 50) / 50;
    const r = Math.round(255 + (255 - 255) * ratio);
    const g = Math.round(165 + (0 - 165) * ratio);
    const b = Math.round(0 + (0 - 0) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  }
};

interface CircularProgressProps {
  percentage?: number;
  setBgColor?: (bgColor: string) => void;
  size?: number;
  fontSize?: number;
}

const CircularProgress: React.FC<CircularProgressProps> = ({
  percentage = 0,
  setBgColor,
  size = 200,
  fontSize = 45,
}) => {
  const [displayPercentage, setDisplayPercentage] = useState(0);
  const [currentColor, setCurrentColor] = useState("#18cec8");

  const STROKE_WIDTH = 10;
  const RADIUS = (size - STROKE_WIDTH) / 2;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  useEffect(() => {
    // Animate the progress
    const duration = 1100;
    const startTime = Date.now();
    const startPercentage = displayPercentage;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const currentValue = startPercentage + (percentage - startPercentage) * easeOutCubic;
      
      const roundedValue = Math.round(currentValue);
      setDisplayPercentage(roundedValue);
      
      const newColor = interpolateColor(roundedValue);
      setCurrentColor(newColor);
      setBgColor?.(newColor);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [percentage, setBgColor, displayPercentage]);

  // Calculate stroke dash offset for progress circle
  const strokeDashoffset = CIRCUMFERENCE - (displayPercentage / 100) * CIRCUMFERENCE;

  return (
    <div
      className="flex justify-center items-center my-5 relative"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="transform rotate-0">
        {/* Thin outer ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={RADIUS}
          stroke="#c4f0ea"
          strokeWidth={STROKE_WIDTH}
          opacity={0.6}
          fill="none"
        />

        {/* Progress background ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={RADIUS}
          stroke="#e3f6ff"
          strokeWidth={STROKE_WIDTH}
          fill="none"
        />

        {/* Progress bar with dynamic color matching percentage - starts from top */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={RADIUS}
          stroke={currentColor}
          strokeWidth={STROKE_WIDTH}
          fill="none"
          strokeDasharray={`${CIRCUMFERENCE}, ${CIRCUMFERENCE}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          className="transition-all duration-300 ease-out"
        />

        {/* Top dot */}
        {percentage === 0 && (
          <circle
            cx={size / 2}
            cy={STROKE_WIDTH / 2 + 1}
            r={5}
            fill="#18cec8"
            strokeWidth={2}
          />
        )}
      </svg>
      
      {/* Percentage text */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className="font-bold transition-colors duration-300"
          style={{ color: currentColor, fontSize: fontSize }}
        >
          {`${displayPercentage}%`}
        </span>
      </div>
    </div>
  );
};

export default CircularProgress;
